import {
    duplicateClauseChecklist,
    IApiAxiosError,
    IClauseChecklistList,
    IOrganization,
    IOrganizationClauseChecklist,
} from "@/api";
import { OrganizationAutocomplete } from "@/components/shared/OrganizationAutocomplete";
import { TextField } from "@/components/shared/TextField";
import { IModalAction, Modal } from "@/components/shared/Modal";
import { Alert, AlertTitle, Stack } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";

interface DuplicateClauseChecklistDialogProps {
    open: boolean;
    onClose: () => void;
    sourceChecklist: IClauseChecklistList | IOrganizationClauseChecklist;
    defaultOrganization?: IOrganization;
}

export const DuplicateClauseChecklistDialog = ({
    open,
    onClose,
    sourceChecklist,
    defaultOrganization,
}: DuplicateClauseChecklistDialogProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [name, setName] = useState(`${sourceChecklist.name} (Copy)`);
    const [organizationId, setOrganizationId] = useState<number | null>(
        defaultOrganization?.id ?? null
    );
    const [error, setError] = useState<string | null>(null);

    const duplicateMutation = useMutation({
        mutationFn: () => {
            if (!organizationId) {
                throw new Error("Organization is required");
            }
            return duplicateClauseChecklist(sourceChecklist.id, {
                name,
                organization_id: organizationId,
            });
        },
        onSuccess: (newChecklist) => {
            queryClient.invalidateQueries({
                queryKey: ["admin-clause-checklists"],
            });
            queryClient.invalidateQueries({
                queryKey: ["admin-organization-details-clause-checklists"],
            });
            navigate(`/admin/checklists/${newChecklist.id}/details`);
            onClose();
        },
        onError: (error) => {
            const err = error as IApiAxiosError;
            setError(
                err.response?.data.detail || "Duplication failed unexpectedly"
            );
        },
    });

    const handleClose = useCallback(() => {
        if (duplicateMutation.isPending) {
            return;
        }
        onClose();
        setError(null);
    }, [duplicateMutation.isPending, onClose]);

    const handleDuplicate = useCallback(() => {
        setError(null);
        duplicateMutation.mutate();
    }, [duplicateMutation]);

    const isValid = name.trim() !== "" && organizationId !== null;

    const actions: IModalAction[] = [
        {
            label: "Duplicate",
            onClick: handleDuplicate,
            variant: "contained",
            disabled: !isValid || duplicateMutation.isPending,
        },
    ];

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Duplicate Checklist"
            subtitle={`Create a copy of "${sourceChecklist.name}"`}
            actions={actions}
            maxWidth="sm"
            isLoading={duplicateMutation.isPending}
        >
            <Stack gap={3}>
                {error && (
                    <Alert severity="error">
                        <AlertTitle>Error</AlertTitle>
                        {error}
                    </Alert>
                )}

                <TextField
                    label="Checklist Name"
                    placeholder="Enter checklist name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                    disabled={duplicateMutation.isPending}
                />

                <OrganizationAutocomplete
                    value={organizationId}
                    onChange={setOrganizationId}
                    required
                    disabled={duplicateMutation.isPending}
                />
            </Stack>
        </Modal>
    );
};
